import React, { useState } from 'react';
import styled from 'styled-components';
import { useSEO } from '../hooks/useSEO';

const PageContainer = styled.div`
  min-height: 80vh;
  padding: var(--spacing-8) 0;
`;

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  text-align: center;
  padding: clamp(var(--spacing-10), 6vw, var(--spacing-14)) 0;
  margin-bottom: var(--spacing-12);
  background: radial-gradient(900px 400px at 15% 0%, #fef7ff 0%, rgba(254,247,255,0.6) 60%, transparent 100%),
              radial-gradient(900px 400px at 85% 100%, #ecfdf5 0%, rgba(236,253,245,0.6) 60%, transparent 100%),
              linear-gradient(135deg, #f8fafc 0%, #f5f3ff 100%);
  border-radius: var(--border-radius-2xl);
  box-shadow: var(--shadow-lg);

  h1 {
    color: var(--color-secondary);
    font-size: var(--font-size-4xl);
    font-weight: 700;
    margin-bottom: var(--spacing-3);
  }

  p {
    max-width: 760px;
    margin: 0 auto;
    color: var(--color-text);
    font-size: var(--font-size-lg);
    opacity: 0.95;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: clamp(var(--spacing-4), 3vw, var(--spacing-8));
  margin-bottom: var(--spacing-12);
`;

const InfoCard = styled.div`
  background: linear-gradient(180deg, #ffffff 0%, #fafafa 100%);
  border: 1px solid rgba(2,6,23,0.06);
  border-radius: var(--border-radius-2xl);
  box-shadow: 0 10px 24px rgba(2,6,23,0.06);
  padding: clamp(var(--spacing-6), 3vw, var(--spacing-8));
  text-align: left;

  .icon {
    font-size: 1.75rem;
    margin-bottom: var(--spacing-3);
  }

  h3 {
    color: var(--color-secondary);
    font-size: var(--font-size-xl);
    font-weight: 600;
    margin-bottom: var(--spacing-2);
  }

  p, ul {
    color: var(--color-text);
    line-height: 1.7;
  }

  ul { padding-left: var(--spacing-4); }
`;

const Section = styled.section`
  margin-bottom: var(--spacing-12);
`;

const SectionTitle = styled.h2`
  color: var(--color-secondary);
  font-size: var(--font-size-2xl);
  font-weight: 700;
  margin-bottom: var(--spacing-4);
`;

const FAQ = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-3);
`;

const QA = styled.details`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-xl);
  padding: var(--spacing-3) var(--spacing-4);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  box-shadow: var(--shadow-sm);

  &[open] {
    box-shadow: var(--shadow);
    border-color: rgba(79,70,229,0.25);
  }

  summary {
    cursor: pointer;
    list-style: none;
    font-weight: 600;
    color: var(--color-secondary);
    outline: none;
  }
  summary::-webkit-details-marker { display: none; }

  .answer {
    margin-top: var(--spacing-2);
    color: var(--color-text);
    line-height: 1.7;
  }
`;

const Note = styled.div`
  background: #f9fafb;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-xl);
  padding: var(--spacing-4);
  color: var(--color-text);
`;

const LeveransRetur: React.FC = () => {
  // For future expansion (e.g., switch FAQ groups)
  const [openAll, setOpenAll] = useState(false);
  useSEO({
    entityType: 'page',
    entityId: 'leverans-retur',
    fallbackTitle: 'Leverans & Retur – Lillaflora',
    fallbackDescription:
      'Vi förklarar leveranstider, frakt och hur du returnerar eller byter en order från Lillaflora.',
    fallbackImage: '/static/images/lillaflora-hero.jpg'
  });

  return (
    <PageContainer>
      <div className="container">
        <Hero>
          <h1>Leverans & Retur</h1>
          <p>
            Här hittar du information om frakt, leveranstider och hur du gör en retur eller ett byte.
            Vi vill att du ska känna dig trygg med ditt köp hos oss.
          </p>
        </Hero>

        <InfoGrid>
          <InfoCard>
            <div className="icon">🚚</div>
            <h3>Leverans</h3>
            <p>
              Normal leveranstid är 1–3 arbetsdagar. Beställningar lagda innan kl 12 skickas samma dag i mån av lagerstatus.
            </p>
            <ul>
              <li>Frakt: 59 kr — fri frakt över 500 kr</li>
              <li>Utlämning hos ombud eller hemleverans beroende på fraktsätt</li>
            </ul>
          </InfoCard>
          <InfoCard>
            <div className="icon">↩️</div>
            <h3>Retur & Byte</h3>
            <p>
              Du har 14 dagars ångerrätt från det att du mottagit varan. Returer ska vara i oanvänt skick och i originalförpackning.
            </p>
            <ul>
              <li>Byte: kontakta oss så hjälper vi dig</li>
              <li>Återbetalning inom 3–7 bankdagar efter mottagen retur</li>
              <li>Returfrakt: 59 kr (avdras vid återbetalning), vid fel/defekt kostnadsfri retur</li>
              <li>Öppetköp 45 dagar</li>
            </ul>
          </InfoCard>
          <InfoCard>
            <div className="icon">📦</div>
            <h3>Hämta i butik</h3>
            <p>
              Välj “Hämta i butik” i kassan om du vill hämta din beställning i Kramfors. Vi meddelar när den är klar att hämtas.
            </p>
          </InfoCard>
        </InfoGrid>

        <Section>
          <SectionTitle>Vanliga frågor</SectionTitle>
          <FAQ>
            <QA open={openAll}>
              <summary>Hur lång är leveranstiden?</summary>
              <div className="answer">
                Normalt 1–3 arbetsdagar. Under högtider och kampanjer kan leveranstiden bli något längre.
              </div>
            </QA>
            <QA open={openAll}>
              <summary>Vad kostar frakten?</summary>
              <div className="answer">
                Frakten kostar 59 kr. Fri frakt vid ordervärde över 500 kr.
              </div>
            </QA>
            <QA open={openAll}>
              <summary>Hur gör jag en retur?</summary>
              <div className="answer">
                Kontakta oss på <a href="mailto:butik@lillaflora.nu">butik(at)lillaflora.nu</a> med ordernummer och orsak. Du får en returetikett via e‑post. Packa varan väl och lämna in hos ombud.
              </div>
            </QA>
            <QA open={openAll}>
              <summary>Kan jag byta storlek/färg?</summary>
              <div className="answer">
                Ja, meddela oss vilken vara du vill byta till så reserverar vi den om den finns i lager.
              </div>
            </QA>
            <QA open={openAll}>
              <summary>Reklamation & transportskada</summary>
              <div className="answer">
                Upptäcker du fel eller skada, kontakta oss snarast (helst inom 48 timmar) med bilder och ordernummer. Vi ordnar ersättning eller omleverans.
              </div>
            </QA>
            <QA open={openAll}>
              <summary>Undantag från returrätten</summary>
              <div className="answer">
                Specialbeställningar och förseglade hygienprodukter där förseglingen brutits omfattas inte av ångerrätten. Levande växter/blomster omfattas normalt inte av öppet köp.
              </div>
            </QA>
          </FAQ>
        </Section>

        <Note>
          Behöver du hjälp? Hör av dig till oss på <a href="mailto:butik@lillaflora.nu">butik(at)lillaflora.nu</a> eller ring 0708555052 så hjälper vi dig gärna.
        </Note>
      </div>
    </PageContainer>
  );
};

export default LeveransRetur;
